import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MdOutlineKeyboardArrowLeft } from "react-icons/md";
import { LiaShoppingBagSolid } from "react-icons/lia";
import { useLocation, useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ApiService from "../services/ApiService";
import PageLoader from "../components/PageLoader";
import Header from "../components/Header";

import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const SearchPage = () => {
  const navigate = useNavigate();
  const [isLoader, setIsLoader] = useState(false);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [recentSearches, setRecentSearches] = useState([]);
  const [debounceTimer, setDebounceTimer] = useState(null);

  // Load recent searches from localStorage on component mount
  useEffect(() => {
    const savedSearches = localStorage.getItem("recentSearches");
    if (savedSearches) {
      setRecentSearches(JSON.parse(savedSearches));
    }
    window.scrollTo(0, 0);
  }, []);

  // Save to recent searches when a successful search is performed
  const saveToRecentSearches = (query) => {
    if (!query.trim()) return;
    
    const updatedSearches = [
      query,
      ...recentSearches.filter(item => item !== query)
    ].slice(0, 10); // Keep only last 10 searches
    
    setRecentSearches(updatedSearches);
    localStorage.setItem("recentSearches", JSON.stringify(updatedSearches));
  };

  // Debounced search function
  const debouncedSearch = (query) => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    
    const newTimer = setTimeout(() => {
      if (query.trim()) {
        getMenuItems(query);
        saveToRecentSearches(query);
      } else {
        setItems([]);
      }
    }, 500); // 500ms delay
    
    setDebounceTimer(newTimer);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    debouncedSearch(value);
  };

  const handleTagClick = (tag) => {
    setSearch(tag);
    getMenuItems(tag);
    saveToRecentSearches(tag);
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem("recentSearches");
  };

  const getMenuItems = async (query = search) => {
    try {
      setIsLoader(true);
      const response = await ApiService.request({
        method: "GET",
        url: "/search",
        data: {
          query: query.trim(),
          page
        },
      });
      const data = response.data;

      if (data.status) {
        setItems(data.data.items);
      } else {
        toast.error(data.message);
        setItems([]);
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Failed to search items");
      setItems([]);
    } finally {
      setIsLoader(false);
    }
  };

  return (
    <>
      {isLoader ? (
        <PageLoader />
      ) : (
        <div className="main-heading container items-details-main">
          <Header title={'Search'} />
          
          <div className="mx-3">
            <input 
              type="search" 
              placeholder="Search Food" 
              value={search}
              onChange={handleSearchChange}
              className="form-control shadow py-2" 
            />
          </div>

          {/* Search Results */}
          {search && (
            <div className="row px-3 my-3">
              {items.length > 0 ? (
                items.map((item) => (
                  <Link to={'/item/'+item._id} key={item._id} className="col-12 col-md-4 mb-3">
                    <div className="border rounded bg-white p-3 h-100">
                      <div className="row align-items-center">
                        <div className="col-8 deal-data">
                          <h6>{item.title}</h6>
                          <p className="text-muted item-des">{item.description}</p>
                          <div className="d-flex below-section">
                            <h6>AED {(item?.discounted_price) ? item?.discounted_price : item?.original_price}</h6>
                          </div>
                        </div>
                        <div className="col-4 text-end">
                          <img
                            src={item.image}
                            alt={item.title}
                            className="item-img item-img-1"
                          />
                        </div>
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="col-12 text-center py-5">
                  <p className="text-muted">No items found for "{search}"</p>
                </div>
              )}
            </div>
          )}

          {/* Recent Searches */}
          {!search && recentSearches.length > 0 && (
            <div className="recent-searches mx-3 mt-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Recent Searches</h5>
                <button 
                  onClick={clearRecentSearches}
                  className="btn btn-sm btn-outline-secondary"
                >
                  Clear All
                </button>
              </div>
              <hr />
              <div className="tags d-flex flex-wrap gap-2">
                {recentSearches.map((tag, index) => (
                  <span 
                    key={index}
                    className="search-tag badge bg-light text-dark p-2 cursor-pointer"
                    onClick={() => handleTagClick(tag)}
                    style={{ cursor: 'pointer' }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* No recent searches message */}
          {!search && recentSearches.length === 0 && (
            <div className="text-center py-5">
              <p className="text-muted">No recent searches</p>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default SearchPage;